/**
 * MascotService — event-driven mascot appearances across AWENI surfaces.
 *
 * Per CLAUDE.md "Mascot & Voice Governance" + Motionov 3-surface design:
 * - ProfessOwl: Hisni (wisdom/knowledge). Cue on first-time-per-day enter.
 * - FoXplorr:   Yawmi  (energy/exploration of habits). Cue on milestones
 *               (athkar 100, 7-day streak, 30-day streak).
 * - NEITHER on AWENI home Hero — competes with the brand-mark.
 *
 * v2 (this file): event scaffold + AthkarCounter milestone hook + speech
 * bubble (T3.4 FLAGSHIP). Only one mascot can appear at a time (anti-
 * double-mascot lock). Auto-dismiss after 5s (longer when a bubble is
 * shown, capped by clamp). Tap-to-dismiss removes both bubble and mascot.
 * Respects prefers-reduced-motion (fade-only, no scale).
 *
 * Public API (event-based; do NOT import the singleton):
 *   window.dispatchEvent(new CustomEvent('aweni:mascot:appear', {
 *     detail: { mascot: 'foxplorr' | 'professowl',
 *               pose: 'eureka' | 'profile_left' | ...,
 *               surface: 'yawmi' | 'hisni',
 *               reason: 'athkar_100' | 'streak_7d' | 'hisni_first_open' | ...,
 *               bubbleKey: 'mascot_streak_7d' }  // i18n key OR raw text
 *   }));
 *
 * Wire in main.ts AFTER bootstrap:
 *   import { mountMascotService } from './services/MascotService';
 *   mountMascotService();
 */
import { hasContext, getContext } from '../context/AweniContext';

type MascotName = 'foxplorr' | 'professowl';
type MascotSurface = 'yawmi' | 'hisni';

interface MascotAppearDetail {
  mascot: MascotName;
  pose?: string;
  surface: MascotSurface;
  reason: string;
  /** Optional text bubble (i18n key OR raw text). v1 ignored — reserved for v2. */
  bubbleKey?: string;
  /** Override auto-dismiss timeout (ms). Default 5000. Clamped to [1500, 12000]. */
  durationMs?: number;
}

const MASCOT_BASE = '/games/shared/assets/mascots';
const FALLBACK_EMOJI: Record<MascotName, string> = {
  foxplorr: '🦊',
  professowl: '🦉',
};
const DEFAULT_POSE: Record<MascotName, string> = {
  foxplorr: 'eureka',
  professowl: 'profile_left',
};
const NAV_HEIGHT_FALLBACK_PX = 80;

let _wired = false;
let _activeEl: HTMLElement | null = null;
let _dismissTimer: ReturnType<typeof setTimeout> | null = null;

function prefersReducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

function dismiss(): void {
  if (_dismissTimer) { clearTimeout(_dismissTimer); _dismissTimer = null; }
  if (!_activeEl) return;
  const el = _activeEl;
  _activeEl = null;
  el.style.opacity = '0';
  el.style.transform = prefersReducedMotion() ? 'none' : 'translateY(8px) scale(.92)';
  // 220ms ~ --motion-duration-200 + small buffer for fade-out completion before remove
  setTimeout(() => { try { el.remove(); } catch { /* already removed */ } }, 220);
}

/** Resolve bubble text. If the value looks like an i18n key and t() returns
 *  a different string, use the translation; otherwise fall back to the raw
 *  value (lets callers pass either a key or literal text). */
function resolveBubbleText(bubbleKey: string): string {
  if (!bubbleKey) return '';
  // Looks like an i18n key: snake_case identifier, no spaces/punctuation.
  const looksLikeKey = /^[a-z][a-z0-9_]*$/i.test(bubbleKey);
  if (looksLikeKey && hasContext()) {
    try {
      const translated = getContext().i18n.t(bubbleKey);
      if (translated && translated !== bubbleKey) return translated;
    } catch { /* fall through to raw */ }
  }
  return bubbleKey;
}

function appear(detail: MascotAppearDetail): void {
  // Anti-double-mascot lock: dismiss current (if any) before showing the new one.
  if (_activeEl) dismiss();

  const reduce = prefersReducedMotion();
  const pose = detail.pose || DEFAULT_POSE[detail.mascot];
  const imgUrl = `${MASCOT_BASE}/${detail.mascot}/${detail.mascot}_${pose}.png`;
  const emoji = FALLBACK_EMOJI[detail.mascot];
  const nav = document.querySelector('[role="tablist"]') as HTMLElement | null;
  const navHeight = nav ? nav.getBoundingClientRect().height : NAV_HEIGHT_FALLBACK_PX;
  const bottomOffsetPx = navHeight + 16;

  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('data-mascot', detail.mascot);
  el.setAttribute('data-surface', detail.surface);
  el.setAttribute('data-reason', detail.reason);
  el.style.cssText = [
    'position:fixed',
    `bottom:calc(${bottomOffsetPx}px + env(safe-area-inset-bottom))`,
    `${detail.surface === 'hisni' ? 'left' : 'right'}:16px`,
    'width:96px;height:96px',
    'z-index:9000',
    'cursor:pointer',
    'opacity:0',
    `transition:opacity var(--motion-duration-base,200ms) var(--motion-ease-standard,ease), transform var(--motion-duration-300,300ms) var(--motion-ease-spring,cubic-bezier(.2,1.2,.3,1))`,
    `transform:${reduce ? 'none' : 'translateY(8px) scale(.92)'}`,
    'pointer-events:auto',
  ].join(';');

  // Image first; fall back to emoji if it 404s.
  const img = document.createElement('img');
  img.alt = `${detail.mascot} mascot`;
  img.src = imgUrl;
  img.style.cssText = 'width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 4px 12px rgba(0,0,0,.25))';
  img.onerror = () => {
    img.remove();
    const span = document.createElement('span');
    span.textContent = emoji;
    span.style.cssText = 'font-size:64px;line-height:96px;text-align:center;display:block;filter:drop-shadow(0 4px 12px rgba(0,0,0,.25))';
    el.appendChild(span);
  };
  el.appendChild(img);

  // Optional speech bubble (T3.4): renders ABOVE the mascot with an arrow
  // pointing down to it. Tap-dismiss on the wrapper removes both.
  // RTL flip is handled by `:dir(rtl)` injected once into <head>.
  let bubbleText = '';
  if (detail.bubbleKey) {
    bubbleText = resolveBubbleText(detail.bubbleKey);
  }
  if (bubbleText) {
    ensureBubbleStylesMounted();
    const bubble = document.createElement('div');
    bubble.className = 'aweni-mascot-bubble';
    bubble.setAttribute('role', 'tooltip');
    bubble.textContent = bubbleText;
    el.appendChild(bubble);
  }

  el.addEventListener('click', () => dismiss());

  document.body.appendChild(el);
  _activeEl = el;

  // Trigger appear animation on next frame.
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'none';
  });

  // Auto-dismiss. Bubble copy needs more reading time → bump default to 7s.
  const defaultMs = bubbleText ? 7000 : 5000;
  const duration = Math.max(1500, Math.min(12000, detail.durationMs ?? defaultMs));
  _dismissTimer = setTimeout(dismiss, duration);
}

let _bubbleStylesMounted = false;

/** Inject bubble styles into <head> on first use. Uses ln tokens with
 *  hardcoded fallbacks so it works before tokens.v1.css loads. The arrow
 *  is a CSS pseudo-element triangle. RTL flips the bubble's anchor side
 *  via :dir(rtl) so the arrow tip stays over the mascot. */
function ensureBubbleStylesMounted(): void {
  if (_bubbleStylesMounted) return;
  _bubbleStylesMounted = true;
  const style = document.createElement('style');
  style.setAttribute('data-aweni-mascot-bubble', '1');
  style.textContent = `
.aweni-mascot-bubble {
  position: absolute;
  bottom: calc(100% + 12px);
  inset-inline-end: 0;
  max-width: 240px;
  min-width: 120px;
  padding: 12px 14px;
  background: var(--ln-bg-card, #ffffff);
  color: var(--ln-text-primary, #0A0A0A);
  font-family: var(--ln-font-ui, system-ui, sans-serif);
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
  border-radius: var(--ln-radius-md, 12px);
  box-shadow: var(--ln-shadow-md, 0 4px 16px rgba(0,0,0,0.12));
  pointer-events: none;
  white-space: normal;
  text-align: start;
  z-index: 1;
}
.aweni-mascot-bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  inset-inline-end: 28px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid var(--ln-bg-card, #ffffff);
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.08));
}
[data-mascot][data-surface="hisni"] .aweni-mascot-bubble {
  inset-inline-start: 0;
  inset-inline-end: auto;
}
[data-mascot][data-surface="hisni"] .aweni-mascot-bubble::after {
  inset-inline-start: 28px;
  inset-inline-end: auto;
}
`;
  document.head.appendChild(style);
}

function isAweniHero(): boolean {
  // Suppress mascots while the AWENI home Hero region is the visible focus
  // (brand-mark competition rule). Heuristic: hash route is /home or empty.
  try {
    const h = (window.location.hash || '').replace(/^#/, '').toLowerCase();
    return h === '' || h === '/' || h === '/home';
  } catch { return false; }
}

export function mountMascotService(): void {
  if (_wired) return;
  _wired = true;
  window.addEventListener('aweni:mascot:appear', ((e: Event) => {
    const detail = (e as CustomEvent<MascotAppearDetail>).detail;
    if (!detail || (detail.mascot !== 'foxplorr' && detail.mascot !== 'professowl')) return;
    if (isAweniHero()) return; // never on AWENI Home Hero
    appear(detail);
  }) as EventListener);
  window.addEventListener('aweni:mascot:dismiss', () => dismiss());
}
